import { HiDownload } from 'react-icons/hi';
import WidgetWrapper from '../WidgetWrapper';
import { DashboardP1Insights } from '../../types/dashboard.types';
import { CurrencyFormat } from '../shared/Formats';
import { downloadCsv } from '../../utils/csvDownload';

type DashboardCampaignHealthWidgetProps = {
  insights: DashboardP1Insights | null;
  compareInsights?: DashboardP1Insights | null;
  compareLabel?: string;
  loading?: boolean;
};

export default function DashboardCampaignHealthWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
}: DashboardCampaignHealthWidgetProps) {
  const campaigns = (insights?.campaignHealth?.campaigns ?? []).slice(0, 5);
  const summary = insights?.campaignHealth?.summary;
  const compareSummary = compareInsights?.campaignHealth?.summary;

  const handleDownload = () => {
    if (!campaigns.length) return;
    downloadCsv('campaign-health', campaigns.map((c) => ({
      Campaign: c.campaignName,
      Status: c.atRisk ? 'At risk' : 'On track',
      'Progress %': `${c.targetProgressPct.toFixed(1)}%`,
      'Total Raised': c.amountCollected,
      Target: c.targetAmount,
      'Still Needed': Math.max(c.targetAmount - c.amountCollected, 0),
    })));
  };

  return (
    <WidgetWrapper
      title="Campaign Health"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-emerald-50/80 via-white/75 to-teal-100/75 dark:from-emerald-900/20 dark:via-slate-900/78 dark:to-teal-900/18 border-emerald-200/50 dark:border-emerald-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="flex justify-end">
            <button
              onClick={handleDownload}
              disabled={!campaigns.length}
              title="Download CSV"
              className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <HiDownload className="h-4 w-4" />
              <span>Download CSV</span>
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Active campaigns</p>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{summary?.activeCampaigns ?? 0}</p>
              {compareSummary && <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareSummary.activeCampaigns}</p>}
            </div>
            <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">At-risk campaigns</p>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{summary?.atRiskCampaigns ?? 0}</p>
              {compareSummary && <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareSummary.atRiskCampaigns}</p>}
            </div>
            <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
              <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Avg progress</p>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{(summary?.avgProgressPct ?? 0).toFixed(2)}%</p>
              {compareSummary && <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareSummary.avgProgressPct.toFixed(2)}%</p>}
            </div>
          </div>

          <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 overflow-hidden">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-primary-50/80 dark:bg-slate-800/80">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Campaign</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Progress</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Raised</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Target</th>
                  <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Still needed</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const stillNeeded = Math.max(campaign.targetAmount - campaign.amountCollected, 0);

                  return (
                    <tr key={campaign.campaignId} className="border-t border-gray-200/60 dark:border-white/10">
                      <td className="px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                        <div className="flex items-center gap-2">
                          <span>{campaign.campaignName}</span>
                          {campaign.atRisk && <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">At risk</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">{campaign.targetProgressPct.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{CurrencyFormat(campaign.amountCollected)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{CurrencyFormat(campaign.targetAmount)}</td>
                      <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{CurrencyFormat(stillNeeded)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
}
